<div class="rpd-container">

  <!-- Header -->
  <div class="rpd-header">
    <div class="flex items-center gap-3 flex-1 min-w-0">
      <div class="rpd-header-icon-wrap">
        <lucide-angular name="folder-key" class="rpd-header-icon"></lucide-angular>
      </div>
      <div class="min-w-0">
        <h2 class="rpd-title">Request Project Access</h2>
        <p class="rpd-subtitle" *ngIf="data.organization">
          Projects for <strong>{{ data.organization }}</strong>
        </p>
      </div>
    </div>
    <button type="button" mat-icon-button class="rpd-close" (click)="onClose()" aria-label="Close">
      <lucide-angular name="x" class="h-5 w-5"></lucide-angular>
    </button>
  </div>

  <!-- Dialog content -->
  <mat-dialog-content class="rpd-content">

    <!-- Loading state -->
    <div *ngIf="isLoading" class="rpd-state-center">
      <div class="rpd-spinner"></div>
      <span class="rpd-state-text">Loading projects...</span>
    </div>

    <!-- Error state -->
    <div *ngIf="!isLoading && hasError && !isSubmitted" class="rpd-state-center">
      <lucide-angular name="alert-circle" class="rpd-state-icon rpd-state-icon--error"></lucide-angular>
      <p class="rpd-state-text">Failed to load projects. Please try again.</p>
      <button type="button" class="rpd-retry-btn" (click)="retryLoad()">
        <lucide-angular name="refresh-cw" class="h-4 w-4 mr-1"></lucide-angular>
        Retry
      </button>
    </div>

    <!-- Success state -->
    <div *ngIf="isSubmitted" class="rpd-state-center rpd-state-success">
      <div class="rpd-success-icon-wrap">
        <lucide-angular name="circle-check" class="rpd-success-icon"></lucide-angular>
      </div>
      <h3 class="rpd-success-title">Request Submitted</h3>
      <p class="rpd-state-text">
        Your access request for {{ selectedProjectsList.length === 1 ? 'this project' : 'these projects' }} has been submitted:
      </p>
      <ul class="rpd-success-list">
        <li *ngFor="let p of selectedProjectsList" class="rpd-success-list-item">
          <lucide-angular name="folder" class="h-3.5 w-3.5 shrink-0"></lucide-angular>
          <span>{{ p.project }}</span>
        </li>
      </ul>
      <div class="rpd-success-reason" *ngIf="accessReason">
        <span class="rpd-success-reason-label">Reason provided:</span>
        <span class="rpd-success-reason-text">{{ accessReason }}</span>
      </div>
      <p class="rpd-success-note">An administrator will review and approve your request shortly.</p>
    </div>

    <!-- Main content -->
    <ng-container *ngIf="!isLoading && !hasError && !isSubmitted">

      <!-- Info banner -->
      <div class="rpd-info-block">
        <lucide-angular name="info" class="rpd-info-icon"></lucide-angular>
        <p class="rpd-info-text">Select projects below and describe your reason. An admin will review your request.</p>
      </div>

      <!-- List section: search + capped table -->
      <div class="rpd-list-section">

        <!-- Search bar -->
        <div class="rpd-search-wrap">
          <div class="rpd-search-bar">
            <lucide-angular name="search" class="rpd-search-icon"></lucide-angular>
            <input
              class="rpd-search-input"
              type="text"
              placeholder="Search projects..."
              [(ngModel)]="searchQuery"
              autocomplete="off"
            />
            <button
              *ngIf="searchQuery"
              type="button"
              class="rpd-search-clear"
              (click)="searchQuery = ''"
              aria-label="Clear search"
            >
              <lucide-angular name="x" class="h-4 w-4"></lucide-angular>
            </button>
          </div>
          <div class="rpd-search-meta" *ngIf="allProjects.length > 0">
            <p class="rpd-search-count">
              {{ filteredProjects.length }} project{{ filteredProjects.length !== 1 ? 's' : '' }}
              <ng-container *ngIf="selectedCount > 0">
                &nbsp;&mdash;&nbsp;<strong class="rpd-selected-label">{{ selectedCount }} selected</strong>
              </ng-container>
            </p>
            <button
              *ngIf="!showAlreadyAccessible && hiddenAccessibleCount > 0"
              type="button"
              class="rpd-accessible-toggle"
              (click)="showAlreadyAccessible = true"
            >
              Show {{ hiddenAccessibleCount }} you already have access to
            </button>
            <button
              *ngIf="showAlreadyAccessible"
              type="button"
              class="rpd-accessible-toggle rpd-accessible-toggle--hide"
              (click)="showAlreadyAccessible = false"
            >
              Hide accessible projects
            </button>
          </div>
        </div>

        <!-- Empty: no projects exist for this org -->
        <div *ngIf="allProjects.length === 0" class="rpd-state-center rpd-state-empty">
          <lucide-angular name="folder-open" class="rpd-state-icon rpd-state-icon--empty"></lucide-angular>
          <p class="rpd-state-text">No projects found for your organization.</p>
        </div>

        <!-- Empty: user already has access to every org project -->
        <div *ngIf="hasNoNewProjects && !showAlreadyAccessible" class="rpd-state-center rpd-state-empty">
          <lucide-angular name="circle-check" class="rpd-state-icon rpd-state-icon--all-access"></lucide-angular>
          <p class="rpd-state-title-sm">You already have access to all available projects.</p>
          <button type="button" class="rpd-accessible-toggle" (click)="showAlreadyAccessible = true">
            Show all projects
          </button>
        </div>

        <!-- Empty: search returns no new results -->
        <div *ngIf="!hasNoNewProjects && allProjects.length > 0 && filteredProjects.length === 0" class="rpd-state-center rpd-state-empty">
          <lucide-angular name="search-x" class="rpd-state-icon rpd-state-icon--empty"></lucide-angular>
          <p class="rpd-state-text">No new projects match your search.</p>
          <p *ngIf="hiddenAccessibleCount > 0" class="rpd-state-hint">
            {{ hiddenAccessibleCount }} project{{ hiddenAccessibleCount !== 1 ? 's' : '' }} hidden — you already have access.
          </p>
        </div>

        <!-- Project table (height-capped, scrolls internally) -->
        <div class="rpd-table-wrap" *ngIf="filteredProjects.length > 0">
          <table class="rpd-table">
            <thead class="rpd-thead">
              <tr>
                <th class="rpd-th rpd-th-check"></th>
                <th class="rpd-th">Project</th>
                <th class="rpd-th rpd-th-status">Status</th>
              </tr>
            </thead>
            <tbody class="rpd-tbody">
              <tr
                *ngFor="let project of filteredProjects; let i = index"
                class="rpd-tr"
                [class.rpd-tr-odd]="i % 2 === 0"
                [class.rpd-tr-selected]="isSelected(project)"
                [class.rpd-tr-disabled]="alreadyHasAccess(project)"
                (click)="toggleSelect(project)"
              >
                <td class="rpd-td rpd-td-check">
                  <input
                    type="checkbox"
                    class="rpd-checkbox"
                    [checked]="isSelected(project) || alreadyHasAccess(project)"
                    [disabled]="alreadyHasAccess(project)"
                    (click)="$event.stopPropagation()"
                    (change)="toggleSelect(project)"
                  />
                </td>

                <td class="rpd-td">
                  <span class="rpd-project-name">{{ project.project }}</span>
                </td>

                <td class="rpd-td rpd-td-status">
                  <ng-container *ngIf="alreadyHasAccess(project); else statusBadgeTpl">
                    <span class="rpd-status-badge rpd-status-has-access">
                      <lucide-angular name="check" class="h-3 w-3 mr-0.5"></lucide-angular>
                      Has Access
                    </span>
                  </ng-container>
                  <ng-template #statusBadgeTpl>
                    <span
                      class="rpd-status-badge"
                      [class.rpd-status-active]="project.is_active !== false"
                      [class.rpd-status-inactive]="project.is_active === false"
                    >
                      {{ project.is_active !== false ? 'Active' : 'Inactive' }}
                    </span>
                  </ng-template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
      <!-- /rpd-list-section -->

      <!-- Reason for access -->
      <div class="rpd-reason-section">
        <div class="rpd-reason-header">
          <label class="rpd-reason-label" for="accessReason">
            Reason for Access Request
            <span class="rpd-reason-required">*</span>
          </label>
          <span class="rpd-reason-count" [class.rpd-reason-count--warn]="accessReason.length >= 450">
            {{ accessReason.length }}/500
          </span>
        </div>
        <textarea
          id="accessReason"
          class="rpd-reason-textarea"
          [(ngModel)]="accessReason"
          placeholder="Describe why you need access to the selected project(s)..."
          rows="3"
          maxlength="500"
        ></textarea>
        <p class="rpd-reason-hint" *ngIf="selectedCount > 0 && !accessReason.trim()">
          <lucide-angular name="alert-circle" class="h-3.5 w-3.5 mr-1 shrink-0"></lucide-angular>
          A reason is required to submit your request.
        </p>
      </div>

    </ng-container>

  </mat-dialog-content>

  <!-- Actions footer -->
  <mat-dialog-actions align="end" class="rpd-actions">
    <ng-container *ngIf="!isSubmitted">
      <button type="button" class="rpd-cancel-btn" (click)="onClose()" [disabled]="isSubmitting">
        Cancel
      </button>
      <button
        type="button"
        class="rpd-submit-btn"
        [disabled]="!canSubmit"
        (click)="submitRequest()"
      >
        <ng-container *ngIf="!isSubmitting">
          <lucide-angular name="send" class="h-4 w-4 mr-1.5"></lucide-angular>
          Submit Request
          <span *ngIf="selectedCount > 0" class="rpd-submit-count">{{ selectedCount }}</span>
        </ng-container>
        <ng-container *ngIf="isSubmitting">
          <div class="rpd-btn-spinner"></div>
          Submitting...
        </ng-container>
      </button>
    </ng-container>
    <ng-container *ngIf="isSubmitted">
      <button type="button" class="rpd-submit-btn rpd-submit-btn--close" (click)="onClose()">
        <lucide-angular name="check" class="h-4 w-4 mr-1.5"></lucide-angular>
        Done
      </button>
    </ng-container>
  </mat-dialog-actions>

</div>
